import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import style from "../partnerProfileModal.module.scss";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import CustomToggleSwitch from "../../../../custom/custom-toggle-switch/CustomToggleSwitch";
import Expeditor from "../../../../../services/Expeditor";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";

const PartnerStatusToggle = ({ item, onStatusChange, onRefresh }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);
  const [isChecked, setIsChecked] = useState(item.state === "Confirmed");
  const [previousChecked, setPreviousChecked] = useState(item.state === "Confirmed");
  const [showActions, setShowActions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [actionType, setActionType] = useState(null); // 'send' | 'respond'

  const decisionRef = useRef(null);

  const isRequestSender = item.senderRecoveryRequest === user.type;
  const hasPendingRequest = item.state === "RecoveryRequest";

  const handleToggle = (checked) => {
    setPreviousChecked(isChecked);
    setIsChecked(checked);

    if (checked && item.state === "Confirmed") {
      setActionType(null);
      setShowActions(false);
    } else if (hasPendingRequest && !isRequestSender) {
      setActionType("respond");
      setShowActions(true);
    } else {
      setActionType("send");
      setShowActions(true);
    }
  };

  useEffect(() => {
    if (showActions && decisionRef.current) {
      setTimeout(() => {
        decisionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
          inline: "nearest",
        });
      }, 100);
    }
  }, [showActions, isChecked]);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      let newState;

      if (actionType === "send") {
        newState = isChecked ? "Confirmed" : "UnActive";
      } else if (actionType === "respond") {
        newState = isChecked ? "Confirmed" : "UnActive";
      }

      await Expeditor.updatePartnerStatus(item.id, newState);
      toast.success(t("partnersPage.statuses.statusUpdated"));
      setShowActions(false);
      setActionType(null);
      onRefresh();

      if (onStatusChange) {
        onStatusChange(newState);
      }
    } catch (error) {
      console.error("Error updating partner status:", error);
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsChecked(previousChecked);
    setShowActions(false);
    setActionType(null);
  };

  const handleAcceptRequest = async () => {
    setIsLoading(true);
    try {
      await Expeditor.updatePartnerStatus(item.id, "Confirmed");
      toast.success(t("partnersPage.statuses.statusUpdated"));
      onRefresh();

      if (onStatusChange) {
        onStatusChange("Confirmed");
      }
    } catch (error) {
      console.error("Error updating partner status:", error);
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    setIsLoading(true);
    try {
      await Expeditor.updatePartnerStatus(item.id, "UnActive");
      toast.success(t("partnersPage.statuses.statusUpdated"));
      onRefresh();

      if (onStatusChange) {
        onStatusChange("UnActive");
      }
    } catch (error) {
      console.error("Error updating partner status:", error);
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusText = () => {
    if (item.state === "RecoveryRequest") {
      if (isRequestSender) {
        return `${t("partnersPage.statuses.companyStatus")}: ${t(
          "partnersPage.statuses.recoveryRequestSent",
        )}`;
      } else {
        return `${t("partnersPage.statuses.companyStatus")}: ${t(
          "partnersPage.statuses.recoveryRequestReceived",
        )}`;
      }
    }

    return isChecked
      ? `${t("partnersPage.statuses.companyStatus")}: ${t("partnersPage.statuses.active")}`
      : `${t("partnersPage.statuses.companyStatus")}: ${t("partnersPage.statuses.inactive")}`;
  };

  const getConfirmationText = () => {
    if (actionType === "send") {
      return isChecked
        ? t("partnersPage.statuses.confirmRestorePartnership")
        : t("partnersPage.statuses.confirmDeactivation");
    } else if (actionType === "respond") {
      return isChecked
        ? t("partnersPage.statuses.confirmAcceptRequest")
        : t("partnersPage.statuses.confirmRejectRequest");
    }
    return t("common.confirmAction");
  };

  return (
    <>
      <div className={style.statusToggle}>
        <span className={style.statusLabel}>{getStatusText()}</span>
          {!hasPendingRequest && (
            <CustomToggleSwitch
              onToggle={handleToggle}
              value={isChecked}
              tooltip={t("tooltips.togglePartnershipStatus")}
              tooltipId="partner-status-toggle-tooltip"
            />
          )}
      </div>

      {hasPendingRequest && !isRequestSender && !showActions && (
        <div className={style.actions} style={{ marginTop: "16px" }}>
          <CustomButton
            text={t("partnersPage.statuses.acceptRequest")}
            onClick={handleAcceptRequest}
            isLoading={isLoading}
            isDark
          />
          <CustomButton
            text={t("partnersPage.statuses.rejectRequest")}
            onClick={handleRejectRequest}
            isLoading={isLoading}
            isDark
            isWhiteBox
          />
        </div>
      )}

      {showActions && (
        <>
          <div ref={decisionRef} className={style.decision} style={{ marginTop: "0" }}>
            <h3>{getConfirmationText()}</h3>
          </div>

          <div className={style.actions}>
            <CustomButton text={t("common.confirm")} onClick={handleConfirm} isLoading={isLoading} isDark />
            <CustomButton
              text={t("common.cancel")}
              onClick={handleCancel}
              isLoading={false}
              isDark
              isWhiteBox
            />
          </div>
        </>
      )}
    </>
  );
};

export default PartnerStatusToggle;
